<script setup lang="ts">
  import RestaurantPosOrdersItem from '@/components/pages/restaurant/pos/RestaurantPosOrdersItem.vue';
  import ScrollIndicator from '@/components/ScrollIndicator.vue';
  import { OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { ITable } from '@/interfaces/ITable';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import ModalPosTableFooter from './table/ModalPosTableFooter.vue';
  import ModalPosTableSummary from './table/ModalPosTableSummary.vue';
  import ModalPosTableHeader from './table/ModalPosTableHeader.vue';
  import { orderMatchesSearch } from '@/utils/orders.utils';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import ModalsCloseButton from '../ModalsCloseButton.vue';
  import { useModal } from '@/composition/use-modal';

  const props = defineProps<{
    table: ITable;
    search?: string | null;
  }>();

  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isSmallDevice = breakpoints.smaller('sm');
  const emit = defineEmits(['close']);

  const ordersByTable = computed(() => {
    if (!restaurantStore.tables) return null;

    let tableOrders = ordersStore.pendingOrdersByType?.[OrderType.Seat] ?? [];

    const ordersByTableId = tableOrders.reduce((acc, order) => {
      if (!acc[order.tableId]) acc[order.tableId] = [];
      acc[order.tableId].push(order);
      return acc;
    }, {} as Record<string, IOrder[]>);

    return ordersByTableId;
  });

  const tabs = computed(() => {
    if (!restaurantStore.tables) return null;

    return restaurantStore.tables
      .filter((t) => t.enabled || ordersByTable.value?.[t.id]?.length)
      .map((t) => ({
        id: t.id,
        label: t.name,
        table: t,
      }));
  });
  const selectedTabIndex = ref(0);
  const selectedTab = computed(() => tabs.value[selectedTabIndex.value]);
  const selectedTable = computed(() => selectedTab.value?.table);

  const selectedOrderIds = ref<number[]>([]);
  const selectedOrderIdsSet = computed(() => new Set(selectedOrderIds.value));

  //#region Search
  const search = ref<string | null>(null);

  watch(
    () => props.search,
    (value) => {
      search.value = value ?? null;
      deselectAllOrders();
    },
    { immediate: true }
  );

  watch(search, () => {
    deselectAllOrders();
  });
  //#endregion

  onMounted(() => {
    if (!props.table || !tabs.value) return;

    selectedTabIndex.value = tabs.value.findIndex(
      (t) => t.id === props.table.id
    );
  });

  // Return ordersByTable, but filtered by search
  const filteredOrdersByTable = computed(() => {
    if (!ordersByTable.value) return null;

    return Object.fromEntries(
      Object.entries(ordersByTable.value).map(([tableId, orders]) => [
        tableId,
        orders.filter((o) => orderMatchesSearch(o, search.value)),
      ])
    );
  });

  const tableOrders = computed(() => {
    if (!selectedTab.value) return null;

    return ordersByTable.value?.[selectedTab.value.id] ?? [];
  });

  const filteredTableOrders = computed(() => {
    if (!selectedTab.value) return null;

    return filteredOrdersByTable.value?.[selectedTab.value.id] ?? [];
  });

  watch(selectedTab, () => {
    deselectAllOrders();
  });

  const selectedOrders = computed(() => {
    return filteredTableOrders.value.filter((o) =>
      selectedOrderIdsSet.value.has(o.id)
    );
  });

  function onOrderCheckboxChange(order: IOrder) {
    if (selectedOrderIdsSet.value.has(order.id)) {
      selectedOrderIds.value = selectedOrderIds.value.filter(
        (id) => id !== order.id
      );
    } else {
      selectedOrderIds.value.push(order.id);
    }
  }

  function selectAllOrders() {
    selectedOrderIds.value = filteredTableOrders.value.map((o) => o.id);
  }

  function deselectAllOrders() {
    selectedOrderIds.value = [];
  }
</script>

<template>
  <div class="modal-pos-table">
    <ModalsCloseButton @close="emit('close')" />
    <div class="modal-inner">
      <div class="nav-container">
        <div class="nav-title">
          <h3>Mesas</h3>
        </div>
        <div class="tabs-container">
          <v-tabs
            v-model="selectedTabIndex"
            background-color="transparent"
            color="primary"
            :vertical="!isSmallDevice"
          >
            <v-tab
              v-for="(tab, index) in tabs"
              :key="tab.id"
              @click="selectedTabIndex = index"
              class="space-x-1"
            >
              <span class="truncate">{{ tab.label }}</span>
              <v-chip
                v-if="filteredOrdersByTable?.[tab.id]?.length"
                color="grey"
                x-small
                dark
              >
                {{ filteredOrdersByTable?.[tab.id]?.length }}
              </v-chip>
            </v-tab>
          </v-tabs>
        </div>
      </div>
      <div class="main-container">
        <ModalPosTableHeader
          :table="selectedTable"
          :orders="filteredTableOrders"
          :search="search"
          @update:search="search = $event"
        />
        <ScrollIndicator :key="selectedTab.label">
          <div class="orders">
            <template v-if="!filteredTableOrders?.length">
              <p class="empty">
                <v-icon>mdi-information</v-icon>
                <span>No hay pedidos en esta mesa</span>
              </p>
            </template>
            <template v-else>
              <div
                class="order-row"
                v-for="order in filteredTableOrders"
                :key="order.id"
                :class="{
                  selected: selectedOrderIdsSet.has(order.id),
                }"
              >
                <v-checkbox
                  class="order-checkbox"
                  color="primary"
                  :value="selectedOrderIdsSet.has(order.id)"
                  @change="onOrderCheckboxChange(order)"
                />
                <RestaurantPosOrdersItem
                  class="order-item"
                  :order="order"
                  :selected="selectedOrderIdsSet.has(order.id)"
                  hide-main-info
                />
              </div>
            </template>
          </div>
        </ScrollIndicator>
        <ModalPosTableFooter
          class="footer"
          :table="selectedTable"
          :table-orders="filteredTableOrders"
          :selected-orders="selectedOrders"
          @select-all="selectAllOrders"
          @deselect-all="deselectAllOrders"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .modal-pos-table {
    @apply tw-flex tw-w-full tw-relative;
    min-height: 100px;

    .modal-inner {
      @apply tw-flex tw-w-full tw-flex-col sm:tw-flex-row;

      .nav-container {
        @apply tw-flex-none tw-flex tw-flex-row sm:tw-flex-col;

        .nav-title {
          @apply tw-px-2 tw-py-1 tw-text-center tw-flex tw-items-center tw-justify-center tw-h-14;
        }

        .tabs-container {
          @apply tw-flex-1 tw-min-w-1 tw-min-h-1 tw-overflow-x-auto tw-overflow-y-auto;

          ::v-deep(.v-slide-group__prev, .v-slide-group__next) {
            display: none !important;
          }
        }
      }

      .main-container {
        @apply tw-flex-1 tw-flex tw-flex-col tw-overflow-y-auto;
        min-width: 1px;

        .orders {
          @apply tw-flex-1 tw-space-y-0.5 tw-px-2 tw-py-4 tw-bg-gray-50 tw-min-h-full tw-rounded-lg;

          .empty {
            @apply tw-flex tw-flex-col tw-items-center tw-space-y-1 tw-text-gray-400 tw-p-4;
          }

          .order-row {
            @apply tw-flex tw-items-center tw-space-x-0;

            .order-checkbox {
              @apply tw-flex-none;
            }

            .order-item {
              @apply tw-flex-1;
            }

            &.selected {
              .order-item {
                @apply tw-ring-1 tw-ring-orange-500;
              }
            }
          }
        }
      }
    }
  }
</style>
